import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { Link } from 'react-router-dom';

const getResponsiveStyles = (screenWidth) => {
    const isSmallScreen = screenWidth < 480;
    
    return {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
            padding: isSmallScreen ? '12px' : '20px',
            overflowY: 'auto',
            boxSizing: 'border-box'
        },
        modal: {
            backgroundColor: '#fff',
            borderRadius: isSmallScreen ? '16px' : '24px',
            maxWidth: '450px',
            width: '100%',
            maxHeight: 'calc(100vh - 24px)',
            padding: isSmallScreen ? '16px 12px' : '24px',
            textAlign: 'right',
            direction: 'rtl',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            gap: isSmallScreen ? '10px' : '15px'
        },
        header: {
            textAlign: 'center',
            flexShrink: 0
        },
        title: {
            margin: '8px 0 0 0',
            color: '#333',
            fontSize: isSmallScreen ? 'clamp(18px, 5vw, 24px)' : '1.4rem',
            fontWeight: 'bold',
            lineHeight: 1.2
        },
        content: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
        },
        intro: {
            fontSize: isSmallScreen ? 'clamp(13px, 3.5vw, 15px)' : '0.95rem',
            color: '#666',
            marginBottom: isSmallScreen ? '10px' : '12px',
            lineHeight: 1.4,
            margin: 0
        },
        scrollBox: {
            backgroundColor: '#f8f9fa',
            padding: isSmallScreen ? '10px 12px' : '12px 15px',
            borderRadius: '10px',
            maxHeight: 'calc(50vh - 280px)',
            minHeight: '60px',
            overflowY: 'auto',
            border: '1px solid #efefef',
            WebkitOverflowScrolling: 'touch',
            boxSizing: 'border-box'
        },
        list: {
            paddingRight: isSmallScreen ? '8px' : '12px',
            paddingLeft: 0,
            margin: 0,
            fontSize: isSmallScreen ? 'clamp(11px, 2.8vw, 13px)' : '0.85rem',
            color: '#555',
            lineHeight: isSmallScreen ? 1.5 : 1.6
        },
        footer: {
            flexShrink: 0,
            textAlign: 'center'
        },
        subText: {
            fontSize: isSmallScreen ? '9px' : '11px',
            color: '#999',
            marginBottom: isSmallScreen ? '10px' : '12px',
            lineHeight: 1.3,
            margin: '0 0 ' + (isSmallScreen ? '10px' : '12px') + ' 0'
        },
        link: {
            color: '#4CAF50',
            textDecoration: 'none',
            margin: '0 2px',
            fontWeight: 'bold'
        },
        button: {
            width: '100%',
            padding: isSmallScreen ? '11px 12px' : '13px 16px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: isSmallScreen ? 'clamp(13px, 3.5vw, 16px)' : '1rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 10px rgba(76, 175, 80, 0.3)',
            transition: 'all 0.2s ease',
            boxSizing: 'border-box',
            minHeight: isSmallScreen ? '44px' : '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }
    };
};

const LegalConsentModal = () => {
    const [showModal, setShowModal] = useState(false);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    useEffect(() => {
        // מאזין לסטטוס המשתמש
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                try {
                    const userRef = doc(db, "users", currentUser.uid);
                    const userSnap = await getDoc(userRef);

                    // אם המשתמש קיים ב-DB, בודק אם הוא כבר אישר את התנאים
                    if (userSnap.exists()) {
                        if (!userSnap.data().acceptedTerms) {
                            setShowModal(true);
                        }
                    } else {
                        // אם המשתמש לא קיים ב-DB בכלל, נציג לו את המודאל
                        setShowModal(true);
                    }
                } catch (error) {
                    console.error("Error checking legal status:", error);
                }
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // מאזין לשינויי גודל מסך
    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleAgree = async () => {
        if (!user) return;

        try {
            const userRef = doc(db, "users", user.uid);

            // שמירה ב-Database (שימוש ב-setDoc עם merge כדי ליצור/עדכן)
            await setDoc(userRef, {
                acceptedTerms: true,
                acceptedDate: serverTimestamp(),
                email: user.email,
                metadata: {
                    platform: 'web-mobile',
                    userAgent: navigator.userAgent
                }
            }, { merge: true });

            setShowModal(false);
            console.log("Consent saved to Firestore successfully");
        } catch (error) {
            console.error("Error saving consent:", error);
            alert("חלה שגיאה בשמירת האישור. אנא נסה שוב.");
        }
    };

    // אם המערכת טוענת או שאין צורך להציג - לא מחזיר כלום
    if (loading || !showModal) return null;

    const dynamicStyles = getResponsiveStyles(screenWidth);

    return (
        <div style={dynamicStyles.overlay}>
            <div style={dynamicStyles.modal}>
                <div style={dynamicStyles.header}>
                    <span style={{ fontSize: '30px' }}>🍳</span>
                    <h2 style={dynamicStyles.title}>ברוכים הבאים ל-CookWithAI</h2>
                </div>

                <div style={dynamicStyles.content}>
                    <p style={dynamicStyles.intro}>לפני שמתחילים לחלץ מתכונים ולבנות את הספר שלך, עליך לאשר את תנאי השימוש:</p>

                    <div style={dynamicStyles.scrollBox}>
                        <ul style={dynamicStyles.list}>
                            <li><strong>אחריות על תוכן:</strong> המתכונים מופקים ע"י בינה מלאכותית (AI) על בסיס סרטונים מהרשת. ייתכנו שגיאות בכמויות, זמנים או הוראות בטיחות. יש להפעיל שיקול דעת.</li>
                            <li><strong>זכויות יוצרים:</strong> האפליקציה אינה הבעלים של התוכן המקורי. זכויות היוצרים על הסרטונים שייכות ליוצרים המקוריים בלבד.</li>
                            <li><strong>פרסומות ומידע:</strong> הנך מאשר הצגת פרסומות מותאמות אישית ושימוש בעוגיות (Cookies) לשיפור השירות.</li>
                            <li><strong>פרטיות:</strong> המידע שאתה שומר (מתכונים מועדפים) נשמר בחשבונך האישי לצורך סנכרון בין מכשירים.</li>
                        </ul>
                    </div>
                </div>

                <div style={dynamicStyles.footer}>
                    <p style={dynamicStyles.subText}>
                        בלחיצה על הכפתור, הנך מאשר את
                        <Link to="/privacy" style={dynamicStyles.link}>מדיניות הפרטיות</Link>
                        ואת
                        <Link to="/terms" style={dynamicStyles.link}>תנאי השימוש</Link>
                    </p>
                    <button onClick={handleAgree} style={dynamicStyles.button}>אני מסכים ומאשר</button>
                </div>
            </div>
        </div>
    );
};

export default LegalConsentModal;